// /academy 3-Spalten-Layout: Modul-Liste · Modul-Inhalt · EVE-Tutor.
// Frame/Spacing nach mockups/12-academy/proposal.html. Auf Desktop (lg+) sind
// die Side-Spalten als feste <aside> sichtbar. Auf Mobile gibt es stattdessen
// eine lg:hidden Top-Toolbar mit "Module"- und "Mentor"-Buttons, die Slide-over-
// Drawer (links bzw. rechts) öffnen — Pattern gespiegelt aus community.tsx
// (Overlay + transform + Backdrop). Auto-Read-Markierung nach 4s im Modul.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { LayoutList, Sparkles } from 'lucide-react';
import { ModuleList } from '../components/academy/module-list';
import { ModuleContent } from '../components/academy/module-content';
import { TutorPanel } from '../components/academy/tutor-panel';
import { useAcademyProgress } from '../hooks/use-academy-progress';
import { toPlainText } from '../lib/academy-content';
import { loadAcademyRaw } from '../lib/academy-loader';
import type { ParsedAcademyModule } from '../lib/academy-content';

type AcademyType = 'trading' | 'crypto';

export default function AcademyPage(): ReactNode {
  const [type, setType] = useState<AcademyType>('trading');
  const [slug, setSlug] = useState<string | null>(null);
  const [tutorCtx, setTutorCtx] = useState<{ title: string; context: string }>({ title: '', context: '' });
  const [listOpen, setListOpen] = useState(false);
  const [tutorOpen, setTutorOpen] = useState(false);
  const { isRead, markRead, saveQuiz } = useAcademyProgress();
  const readTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Modul wählen schließt den Mobile-Listen-Drawer (setListOpen ist stabil → keine Dep).
  const onSelect = useCallback((s: string) => {
    setSlug(s);
    setListOpen(false);
  }, []);
  const onTypeChange = useCallback((t: AcademyType) => {
    setType(t);
    setSlug(null);
  }, []);

  const onLoaded = useCallback(
    (mod: ParsedAcademyModule) => {
      void loadAcademyRaw(mod.meta.type, mod.meta.slug)
        .then((raw) => setTutorCtx({ title: mod.meta.title, context: toPlainText(raw) }))
        .catch((e) => console.error('[academy] loadAcademyRaw failed', e));
      if (readTimer.current) clearTimeout(readTimer.current);
      readTimer.current = setTimeout(() => void markRead(mod.meta.slug), 4000);
    },
    [markRead],
  );

  useEffect(
    () => () => {
      if (readTimer.current) clearTimeout(readTimer.current);
    },
    [],
  );

  return (
    <div className="flex h-full flex-col lg:grid lg:grid-cols-[280px_1fr_360px] lg:gap-0">
      {/* ── Mobile-Toolbar (lg:hidden): öffnet Listen- / Mentor-Drawer ── */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-700/40 px-3 py-2.5 lg:hidden">
        <button
          type="button"
          onClick={() => setListOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700/50 bg-slate-800/40 px-3 py-1.5 text-[12.5px] font-medium text-slate-200 hover:border-slate-600/60"
        >
          <LayoutList size={14} />
          Module
        </button>
        {slug && (
          <button
            type="button"
            onClick={() => setTutorOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[12.5px] font-semibold text-amber-300 hover:bg-amber-500/15"
          >
            <Sparkles size={14} />
            Mentor
          </button>
        )}
      </div>

      {/* ── Desktop-Spalte links: Modul-Liste ── */}
      <aside className="hidden border-r border-slate-700/40 p-4 lg:block">
        <ModuleList type={type} activeSlug={slug} isRead={isRead} onSelect={onSelect} onTypeChange={onTypeChange} />
      </aside>

      {/* ── Mobile-Drawer links: Modul-Liste (nur gemountet wenn offen) ── */}
      {listOpen && (
        <>
          <div
            aria-hidden
            onClick={() => setListOpen(false)}
            className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          />
          <aside className="fixed inset-y-0 left-0 z-40 w-[280px] overflow-y-auto border-r border-slate-700/40 bg-[#0a0a0f] p-4 lg:hidden">
            <ModuleList
              type={type}
              activeSlug={slug}
              isRead={isRead}
              onSelect={onSelect}
              onTypeChange={onTypeChange}
            />
          </aside>
        </>
      )}

      {/* ── Zentrale Spalte: Modul-Inhalt ── */}
      <main className="min-h-0 flex-1 overflow-y-auto lg:flex-none">
        <ModuleContent type={type} slug={slug} onLoaded={onLoaded} />
      </main>

      {/* ── Desktop-Spalte rechts: EVE-Tutor ── */}
      <aside className="hidden border-l border-slate-700/40 lg:block">
        {slug ? (
          <TutorPanel
            key={slug ?? ''}
            moduleTitle={tutorCtx.title}
            moduleContext={tutorCtx.context}
            onQuizResult={(passed, score) => {
              if (slug) void saveQuiz(slug, passed, score);
            }}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-4 text-center text-[13px] text-slate-500">
            Öffne ein Modul, um EVE als Mentor zu nutzen.
          </div>
        )}
      </aside>

      {/* ── Mobile-Drawer rechts: EVE-Tutor (nur gemountet wenn offen) ── */}
      {tutorOpen && (
        <>
          <div
            aria-hidden
            onClick={() => setTutorOpen(false)}
            className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          />
          <aside className="fixed inset-y-0 right-0 z-40 w-[320px] overflow-y-auto border-l border-slate-700/40 bg-[#0a0a0f] lg:hidden">
            {slug ? (
              <TutorPanel
                key={slug ?? ''}
                moduleTitle={tutorCtx.title}
                moduleContext={tutorCtx.context}
                onQuizResult={(passed, score) => {
                  if (slug) void saveQuiz(slug, passed, score);
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center px-4 text-center text-[13px] text-slate-500">
                Öffne ein Modul, um EVE als Mentor zu nutzen.
              </div>
            )}
          </aside>
        </>
      )}
    </div>
  );
}
